import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { toast } from "sonner";
import { User, Mail, Bell, Shield, Save } from "lucide-react";

const roleLabels = {
  SUPERADMIN: "Super Admin",
  GESTION: "Gestión",
  COMERCIAL: "Comercial",
  TECNICO: "Técnico",
};

const roleColors = {
  SUPERADMIN: "bg-purple-600",
  GESTION: "bg-[#03BEE4]",
  COMERCIAL: "bg-[#42E695]",
  TECNICO: "bg-orange-500",
};

const ProfilePage = () => {
  const { user, updateNotifications } = useAuth();

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "US";
  };

  const handleNotificationChange = async (type, value) => {
    const emailValue = type === "email" ? value : user.notification_email;
    const inappValue = type === "inapp" ? value : user.notification_inapp;

    const result = await updateNotifications(emailValue, inappValue);
    if (result.success) {
      toast.success("Preferencias actualizadas");
    } else {
      toast.error(result.error);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
          <User className="h-8 w-8 text-[#03BEE4]" />
          Mi Perfil
        </h1>
        <p className="text-slate-400 mt-1">
          Gestiona tu información y preferencias
        </p>
      </div>

      {/* Profile Card */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <User className="h-5 w-5 text-[#03BEE4]" />
            Información Personal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <Avatar className="h-20 w-20 bg-[#1B1B4B] border-2 border-[#42E695]">
              <AvatarFallback className="bg-[#42E695] text-[#04042C] text-2xl font-bold">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <h2 className="text-xl font-heading font-semibold text-white">{user.name}</h2>
              <div className="flex items-center gap-2 text-slate-400">
                <Mail className="h-4 w-4" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-slate-400" />
                <div className="flex gap-2">
                  {user.roles.map((role) => (
                    <Badge
                      key={role}
                      className={`${roleColors[role]} text-white border-none`}
                    >
                      {roleLabels[role]}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#03BEE4]" />
            Preferencias de Notificaciones
          </CardTitle>
          <CardDescription className="text-slate-400">
            Configura cómo quieres recibir las notificaciones
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#03BEE4]/20 flex items-center justify-center">
                <Mail className="h-5 w-5 text-[#03BEE4]" />
              </div>
              <div>
                <Label className="text-white font-medium">Notificaciones por Email</Label>
                <p className="text-sm text-slate-400">
                  Recibe alertas y actualizaciones en tu correo
                </p>
              </div>
            </div>
            <Switch
              checked={user.notification_email}
              onCheckedChange={(v) => handleNotificationChange("email", v)}
              data-testid="notification-email-toggle"
            />
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#42E695]/20 flex items-center justify-center">
                <Bell className="h-5 w-5 text-[#42E695]" />
              </div>
              <div>
                <Label className="text-white font-medium">Notificaciones In-App</Label>
                <p className="text-sm text-slate-400">
                  Recibe notificaciones dentro de la plataforma
                </p>
              </div>
            </div>
            <Switch
              checked={user.notification_inapp}
              onCheckedChange={(v) => handleNotificationChange("inapp", v)}
              data-testid="notification-inapp-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading">Información de la Cuenta</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-[#1E1E5F]">
              <span className="text-slate-400">ID de Usuario</span>
              <span className="text-white font-mono text-sm">{user.id.slice(0, 16)}...</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-[#1E1E5F]">
              <span className="text-slate-400">Roles</span>
              <div className="flex gap-1 flex-wrap justify-end">
                {user.roles?.map(r => (
                  <Badge key={r} className={`${roleColors[r] || 'bg-slate-600'} text-white border-none`}>
                    {roleLabels[r] || r}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-[#1E1E5F]">
              <span className="text-slate-400">Fecha de Registro</span>
              <span className="text-white">
                {new Date(user.created_at).toLocaleDateString("es-ES")}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-400">Estado</span>
              <Badge className="bg-[#42E695]/20 text-[#42E695] border-[#42E695]/30">
                Activo
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
